import { useDeferredValue, useEffect, useMemo, useState } from 'react'
import { Scissors } from 'lucide-react'
import {
  useAllCreatedProducts,
  useCatalogEntriesByIds,
  useSplitPreview,
  useSplitProduct,
} from '@/hooks/useProducts'
import type { CreatedProduct } from '@/types/products'
import { inHandProducts } from '@/lib/productSupersession'
import {
  divisionSuffixOf,
  evenVolumeSplit,
  nextDivisionSuffixes,
} from '@/lib/productSplit'
import {
  ReasonField,
  SPLIT_REASONS,
  composeReason,
  reasonIsComplete,
} from '@/components/procedure/ReasonField'
import { SplitLimsPreview } from '@/components/procedure/SplitLimsPreview'
import { MissingLabValues } from '@/components/procedure/MissingLabValues'
import {
  manualInputsPayload,
  mergeMissingRows,
  missingLabValueRows,
  previewGate,
  type MissingLabValueRow,
} from '@/lib/limsInputs'
import { limsColumnSummary } from '@/lib/splitPreview'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { dinSearchText } from '@/lib/isbtDin'


/**
 * Split Product — divide one already-created product into N aliquots.
 *
 * Fidelity matches Create/Receive (screenshot-level v1, no DigiTrax
 * integration on this pass either): pick the parent by DIN/code, choose how
 * many pieces and how much volume to divide, review the computed division
 * suffix and the recalculated lab results per child, then split. The suffix
 * algorithm and the even-volume division are pure/tested in `productSplit.ts`
 * — this component is just the form around them.
 *
 * The source volume is the parent's own recorded volume and is READ-ONLY
 * here — it is a property of the bag, not something to retype per split.
 * Correcting a stale figure is Update product's job.
 *
 * Two distributions: Even divides the source equally and locks the results,
 * Manual lets each piece be typed. Both are validated to add up to the source
 * exactly (100 mL → 40 + 40 + 20), which is why there is no "volume left"
 * field any more — a split that balances leaves nothing behind.
 *
 * The lab results are NOT computed here. `SplitLimsPreview` at the bottom of
 * the dialog shows what each aliquot would be given, what the source held, and
 * the formula between them — every figure and every formula string answered by
 * the server, which is also what will store them. A preview computed in the
 * browser would be a second implementation of the standard's dosing maths,
 * free to disagree with the saved result, which is the thing z8nrz7dkvf
 * removed.
 */
export function SplitProductDialog({
  open,
  onClose,
  submissionId,
  formDefinitionId,
  procedureId,
  patientMrn,
  onSplit,
  onOrderTest,
}: {
  open: boolean
  onClose: () => void
  submissionId: string
  formDefinitionId: string
  procedureId: string
  patientMrn: string
  onSplit: (children: CreatedProduct[], parent: CreatedProduct, parentFullyConsumed: boolean) => void
  /** Open the laboratory order dialog, for a lab value the forms do not hold. */
  onOrderTest?: () => void
}) {
  const splitProduct = useSplitProduct()
  // Only while open: this dialog is always mounted on the case page (z8nrz7e1c2).
  const allProducts = useAllCreatedProducts(open)
  // Only the parent's entry is needed, and it is asked for by id: the ISBT
  // catalog is 14,911 rows and paged, so it cannot be scanned for one row.
  const catalog = useCatalogEntriesByIds(allProducts.data?.map(p => p.catalogEntryId) ?? [])
  // Both must land before the list shows: the catalog lookup waits on the
  // product list, and a parent picked early would read its base code as ''. A
  // write while closed only marks the list stale, so a refetch counts too.
  const productsLoading =
    allProducts.isLoading ||
    (allProducts.isFetching && allProducts.isStale) ||
    catalog.isLoading

  const [search, setSearch] = useState('')
  const [parentId, setParentId] = useState<string | null>(null)
  const [count, setCount] = useState(2)
  /**
   * Even divides the source equally and locks the results; Manual lets each
   * piece be typed. Either way the pieces must add up to the source volume —
   * material does not appear or vanish in a split.
   */
  const [mode, setMode] = useState<'even' | 'manual'>('even')
  /** Manual entries, one per aliquot, as typed. */
  const [manualVolumes, setManualVolumes] = useState<string[]>([])
  const [reason, setReason] = useState('')
  const [reasonDetail, setReasonDetail] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /** Lab values typed for inputs the forms do not hold, by system name. */
  const [labValues, setLabValues] = useState<Record<string, string>>({})
  /** Every missing-input row seen for this parent, so a satisfied one keeps its input. */
  const [labRows, setLabRows] = useState<MissingLabValueRow[]>([])

  function reset() {
    setSearch('')
    setParentId(null)
    setCount(2)
    setMode('even')
    setManualVolumes([])
    setReason('')
    setReasonDetail('')
    setError(null)
    setLabValues({})
    setLabRows([])
  }

  function handleOpenChange(next: boolean) {
    if (!next) {
      reset()
      onClose()
    }
  }

  // Superseded products are history, not bags (z8nrz7e72t); nor is an
  // original already divided by a split (z8nrz7g2wm).
  const candidates = inHandProducts(allProducts.data ?? []).filter(p => {
    if (p.patientMrn !== patientMrn) return false
    const q = dinSearchText(search).toLowerCase()
    if (!q) return true
    return p.din.toLowerCase().includes(q) || p.productDescriptionCode.toLowerCase().includes(q)
  })
  const parent = candidates.find(p => p.id === parentId) ?? null
  const baseCode = parent
    ? (catalog.byId.get(parent.catalogEntryId)?.productDescriptionCode ?? '')
    : ''
  const currentSuffix = parent ? divisionSuffixOf(parent.productDescriptionCode, baseCode) : ''

  /** The source volume, read-only: it comes from the product, not the operator. */
  const sourceVolume = parent?.volumeMl ?? 0

  // Manual entries follow the aliquot count and start from the even split, so
  // switching to Manual is an edit of a sensible starting point rather than a
  // blank grid to fill in.
  useEffect(() => {
    setManualVolumes(evenVolumeSplit(sourceVolume, count).map(v => String(v)))
  }, [sourceVolume, count, parent?.id])

  let suffixError: string | null = null
  let suffixes: string[] = []
  try {
    suffixes = parent ? nextDivisionSuffixes(currentSuffix, count) : []
  } catch (e) {
    suffixError = e instanceof Error ? e.message : 'Could not compute division suffixes.'
  }

  const perChildVolume = useMemo(() => {
    if (mode === 'even') return evenVolumeSplit(sourceVolume, count)
    return Array.from({ length: count }, (_, i) => Number(manualVolumes[i]) || 0)
  }, [mode, sourceVolume, count, manualVolumes])

  const totalChildVolume = Math.round(perChildVolume.reduce((a, b) => a + b, 0) * 100) / 100
  /** Float noise only — 40 + 40 + 20 must count as 100. */
  const volumesBalance = Math.abs(totalChildVolume - sourceVolume) < 0.005
  const volumeError =
    sourceVolume <= 0
      ? 'This product has no recorded volume. Set one from Update product before splitting.'
      : !volumesBalance
        ? `The pieces add up to ${totalChildVolume} mL but the source holds ${sourceVolume} mL.`
        : null

  /**
   * The server's answer to "what would this split do to the lab results".
   *
   * Asked for whenever a parent and workable volumes are on screen, not only
   * when the panel is expanded: a source product carrying no LIMS results has
   * to say so in the table above, where the operator is looking, rather than
   * behind a disclosure they have no reason to open.
   */
  // Not memoised: TanStack hashes query keys structurally, so a fresh object
  // on every render is the same key and does not refetch.
  // Deferred, so typing a lab value does not fire a request per keystroke ahead
  // of the input itself.
  const manualInputs = manualInputsPayload(useDeferredValue(labValues))
  const preview = useSplitPreview(
    parent?.id ?? null,
    {
      submissionId,
      procedureId,
      manualInputs,
      children: suffixes.map((suffix, i) => ({
        productDescriptionCode: `${baseCode}${suffix}`,
        volumeMl: perChildVolume[i] || undefined,
      })),
    },
    !!parent && suffixes.length > 0,
  )
  // Matched by POSITION, not by product code: the server echoes the children
  // back in the order they were sent, and two aliquots of a lineage can carry
  // the same code only if the suffix algorithm has gone wrong — which is not
  // the moment for the LIMS column to quietly show the wrong row.
  const previewChildren = preview.data?.children ?? []

  // Lab values nothing supplied. They no longer block the split: the operator
  // can type them, order the test, or go ahead and have those figures left
  // blank (the audit trail records which).
  const inputsNotFound = preview.data?.inputsNotFound
  const latestRows = useMemo(() => missingLabValueRows(inputsNotFound), [inputsNotFound])
  useEffect(() => {
    setLabRows(shown => mergeMissingRows(shown, latestRows))
  }, [latestRows])
  const stillMissing = useMemo(() => new Set(latestRows.map(r => r.key)), [latestRows])
  // Until the preview has answered for THIS parent and these volumes, nothing
  // says the lab values exist — an empty list while loading is not a pass.
  const gate = previewGate(preview)
  const canSplit =
    !!parent &&
    count >= 1 &&
    !suffixError &&
    !volumeError &&
    gate.ready &&
    reasonIsComplete(reason, reasonDetail)

  async function handleSplit() {
    if (!parent || !canSplit) return
    setSaving(true)
    setError(null)
    try {
      // No DINs: every aliquot carries the parent's DIN and flag, set by the
      // server (z8nrz7fxwh). The division suffix on the code tells them apart.
      const created = await splitProduct.mutateAsync({
        parentId: parent.id,
        // No remainder: the pieces are validated to consume the source
        // exactly, so the parent is always fully divided.
        payload: {
          submissionId,
          formDefinitionId,
          procedureId,
          reason: composeReason(reason, reasonDetail),
          // Gaps only: the server keeps any value a form holds.
          manualInputs: manualInputsPayload(labValues),
          // No `limsValues`. The server recalculates every aliquot's results
          // from the parent's own stored measurements — a dose figure is not
          // something a client gets to assert. See
          // cgt-backend/src/products/lims-calculations.ts.
          children: suffixes.map((suffix, i) => ({
            productDescriptionCode: `${baseCode}${suffix}`,
            volumeMl: perChildVolume[i] || undefined,
          })),
        },
      })
      const parentSnapshot = parent
      handleOpenChange(false)
      onSplit(created, parentSnapshot, true)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to split product.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {/* The recalculation panel is tall when expanded — without a scroll the
          footer buttons leave the viewport on a laptop. */}
      <DialogContent className='max-h-[90vh] overflow-y-auto sm:max-w-3xl'>
        <DialogHeader>
          <DialogTitle>Split product</DialogTitle>
          <DialogDescription>
            Find the product to divide, choose how many aliquots and how much volume to divide, then split.
          </DialogDescription>
        </DialogHeader>

        {!parent ? (
          <div className='space-y-2'>
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder='Scan or type the DIN or product code'
              aria-label='Find product to split'
              className='font-mono'
              autoFocus
            />
            <div className='max-h-64 overflow-y-auto rounded-lg border'>
              {productsLoading ? (
                <p className='p-4 text-sm text-muted-foreground'>Loading products…</p>
              ) : candidates.length === 0 ? (
                <p className='p-4 text-sm text-muted-foreground'>
                  {patientMrn ? 'No products found for this patient.' : 'No patient on this form.'}
                </p>
              ) : (
                candidates.map(p => (
                  <button
                    key={p.id}
                    type='button'
                    onClick={() => {
                      setParentId(p.id)
                      setLabValues({})
                      setLabRows([])
                    }}
                    className='flex w-full items-center justify-between border-b px-3 py-2 text-left text-sm last:border-b-0 hover:bg-accent'
                  >
                    <span className='font-mono'>{p.din}</span>
                    <span className='text-muted-foreground'>{p.productDescriptionCode || '—'}</span>
                  </button>
                ))
              )}
            </div>
          </div>
        ) : (
          <div className='space-y-4'>
            <div className='flex items-center justify-between rounded-lg border bg-muted/20 px-3 py-2 text-sm'>
              <div>
                <span className='font-mono font-medium'>{parent.din}</span>
                <span className='ml-2 text-muted-foreground'>{parent.productDescriptionCode || '—'}</span>
              </div>
              <Button
                variant='ghost'
                size='sm'
                onClick={() => {
                  setParentId(null)
                  setLabValues({})
                  setLabRows([])
                }}
              >
                Change
              </Button>
            </div>

            <div className='grid grid-cols-2 gap-4'>
              <div className='space-y-1'>
                <label className='text-sm font-medium' htmlFor='split-count'>
                  Number of aliquots
                </label>
                <Input
                  id='split-count'
                  type='number'
                  min={1}
                  value={count}
                  onChange={e => setCount(Math.max(1, Math.trunc(Number(e.target.value) || 1)))}
                />
              </div>
              <div className='space-y-1'>
                <label className='text-sm font-medium' htmlFor='split-source-volume'>
                  Source volume (mL)
                </label>
                {/* Read-only: it is the product's recorded volume, not an
                    operator input. Correcting it is Update product's job. */}
                <Input
                  id='split-source-volume'
                  value={sourceVolume > 0 ? String(sourceVolume) : 'Not recorded'}
                  readOnly
                  disabled
                  className='bg-muted/40'
                />
              </div>
            </div>

            <div className='space-y-2'>
              <p className='text-sm font-medium'>Distribution</p>
              <div className='flex w-fit overflow-hidden rounded-lg border'>
                {(['even', 'manual'] as const).map(m => (
                  <button
                    key={m}
                    type='button'
                    onClick={() => setMode(m)}
                    className={
                      'px-3 py-1.5 text-sm transition-colors ' +
                      (mode === m
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-background hover:bg-accent')
                    }
                  >
                    {m === 'even' ? 'Even' : 'Manual'}
                  </button>
                ))}
              </div>
              <p className='text-xs text-muted-foreground'>
                {mode === 'even'
                  ? 'The source volume is divided equally; the piece volumes are locked.'
                  : 'Enter each piece. They must add up to the source volume.'}
              </p>
            </div>

            <ReasonField
              presets={SPLIT_REASONS}
              reason={reason}
              detail={reasonDetail}
              onReasonChange={setReason}
              onDetailChange={setReasonDetail}
              hint='Recorded on every aliquot in the audit trail.'
            />

            {suffixError && <p className='text-sm text-destructive'>{suffixError}</p>}
            {volumeError && <p className='text-sm text-destructive'>{volumeError}</p>}

            <div className='overflow-hidden rounded-lg border'>
              <table className='w-full text-sm'>
                <thead className='bg-muted/30 text-left text-xs uppercase text-muted-foreground'>
                  <tr>
                    <th className='px-3 py-2'>Product code</th>
                    <th className='px-3 py-2'>Volume (mL)</th>
                    <th className='px-3 py-2'>Scaled LIMS totals</th>
                  </tr>
                </thead>
                <tbody>
                  {suffixes.map((suffix, i) => (
                    <tr key={suffix} className='border-t'>
                      <td className='px-3 py-2 font-mono'>{baseCode}{suffix}</td>
                      <td className='px-3 py-2'>
                        {mode === 'manual' ? (
                          <Input
                            type='number'
                            min={0}
                            step='any'
                            aria-label={`Volume for ${baseCode}${suffix}`}
                            value={manualVolumes[i] ?? ''}
                            onChange={e =>
                              setManualVolumes(prev => {
                                const next = [...prev]
                                next[i] = e.target.value
                                return next
                              })
                            }
                            className='h-8 max-w-[7rem]'
                          />
                        ) : (
                          (perChildVolume[i] || '—')
                        )}
                      </td>
                      {/* A count and a pointer, not a figure: singling out
                          one of fifteen results here would invite it to be
                          read as "the" dose. The figures, and the formulas
                          behind them, are in the panel below. */}
                      <LimsSummaryCell
                        summary={limsColumnSummary(
                          preview.data,
                          previewChildren[i],
                          preview.isLoading,
                        )}
                      />
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className='border-t bg-muted/20 text-xs'>
                    <td className='px-3 py-2 font-medium'>Total</td>
                    <td className={'px-3 py-2 font-medium ' + (volumesBalance ? '' : 'text-destructive')}>
                      {totalChildVolume} / {sourceVolume} mL
                    </td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>

            <MissingLabValues
              rows={labRows}
              stillMissing={stillMissing}
              values={labValues}
              onChange={(key, text) => setLabValues(v => ({ ...v, [key]: text }))}
              onOrderTest={onOrderTest}
            />

            <SplitLimsPreview
              preview={preview.data}
              isLoading={preview.isLoading}
              error={preview.error}
            />
          </div>
        )}

        {parent && suffixes.length > 0 && !gate.ready && gate.message && (
          <p className={preview.isError ? 'text-sm text-destructive' : 'text-sm text-muted-foreground'}>
            {gate.message}
          </p>
        )}

        {error && <p className='text-sm text-destructive'>{error}</p>}

        <DialogFooter>
          <Button variant='outline' onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSplit} disabled={!canSplit || saving}>
            <Scissors className='mr-2 h-4 w-4' />
            {saving ? 'Splitting…' : 'Split Product'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * The LIMS column of the aliquot table. Split out only so the tone-to-class
 * mapping does not sit inline inside a `<td>` in the middle of the form.
 */
function LimsSummaryCell({
  summary,
}: {
  summary: { text: string; tone: 'muted' | 'warning' }
}) {
  return (
    <td
      className={
        'px-3 py-2 ' + (summary.tone === 'warning' ? 'text-amber-700' : 'text-muted-foreground')
      }
    >
      {summary.text}
    </td>
  )
}
